import { BarChart, Business, People, ShoppingCart } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Container,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AnalyticsTab from "../components/dashboard/AnalyticsTab";
import CampusesTab from "../components/dashboard/CampusesTab";
import OrdersTab from "../components/dashboard/OrdersTab";
import Header from "../components/layout/Header";
import { useAppState } from "../contexts/AppStateContext";
import { useAuth } from "../contexts/AuthContext";
import { useDashboardData } from "../hooks/useDashboardData";
import type { Order } from "../types/api";

const Dashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { state, dispatch } = useAppState();
  const [activeTab, setActiveTab] = useState<number>(() => {
    const saved = localStorage.getItem("dashboard-active-tab");
    return saved ? parseInt(saved, 10) : 0;
  });
  const [campusFilter, setCampusFilter] = useState<string[]>([]);

  // Use custom hook for data fetching
  const {
    campusesLoading,
    campusesError,
    analytics,
    analyticsLoading,
    analyticsError,
    orders,
    ordersLoading,
    ordersError,
    page,
    totalPages,
    statusFilter,
    setPage,
    setStatusFilter,
  } = useDashboardData();

  // Campus options for filters (sorted alphabetically by key)
  const campusOptions = state.campuses
    .map((campus) => ({
      id: campus.id.toString(),
      key: campus.name,
      name: campus.name,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));

  const handleCampusSelect = (campusId: string): void => {
    dispatch({ type: "SELECT_CAMPUS", payload: campusId });
    dispatch({ type: "SET_VIEW", payload: "center" });
    navigate(`/center/${campusId}`);
  };

  // Handle order click - navigate with state for performance
  const handleOrderClick = (order: Order) => {
    const packageDetails = state.packages.find(
      (pkg) => pkg.packageId === order.packageId
    );
    navigate(`/order/${order.id}`, {
      state: {
        order,
        package: packageDetails,
      },
    });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Header />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Welcome Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: "primary.light",
                color: "white",
              }}
            >
              <People />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 700, mb: 0.5 }}
              >
                Welcome back, {currentUser?.email?.split("@")[0] || "User"}!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage photo uploads and orders across all campuses
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => {
            setActiveTab(newValue);
            localStorage.setItem("dashboard-active-tab", String(newValue));
          }}
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            id="analytics-tab"
            aria-controls="analytics-tabpanel"
            icon={<BarChart />}
            label="Analytics"
            iconPosition="start"
          />
          <Tab
            id="campuses-tab"
            aria-controls="campuses-tabpanel"
            icon={<Business />}
            label="Campuses"
            iconPosition="start"
          />
          <Tab
            id="orders-tab"
            aria-controls="orders-tabpanel"
            icon={<ShoppingCart />}
            label={`Orders (${orders.length})`}
            iconPosition="start"
          />
        </Tabs>

        {campusesError && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {campusesError}
          </Alert>
        )}

        {/* Analytics Tab */}
        <Box
          role="tabpanel"
          hidden={activeTab !== 0}
          id="analytics-tabpanel"
          aria-labelledby="analytics-tab"
        >
          <AnalyticsTab
            analytics={analytics}
            loading={analyticsLoading}
            error={analyticsError}
          />
        </Box>

        {/* Campuses Tab */}
        <Box
          role="tabpanel"
          hidden={activeTab !== 1}
          id="campuses-tabpanel"
          aria-labelledby="campuses-tab"
        >
          <CampusesTab
            campuses={state.campuses}
            loading={campusesLoading}
            onCampusSelect={handleCampusSelect}
          />
        </Box>

        {/* Orders Tab */}
        <Box
          role="tabpanel"
          hidden={activeTab !== 2}
          id="orders-tabpanel"
          aria-labelledby="orders-tab"
        >
          <OrdersTab
            orders={orders}
            packages={state.packages}
            campuses={campusOptions}
            loading={ordersLoading}
            error={ordersError}
            campusFilter={campusFilter}
            statusFilter={statusFilter}
            page={page}
            totalPages={totalPages}
            onCampusFilterChange={setCampusFilter}
            onStatusFilterChange={setStatusFilter}
            onPageChange={setPage}
            onOrderClick={handleOrderClick}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;
